import { type MouseEvent, useCallback, useMemo, useRef } from 'react';

import { type ClipSpriteSheet } from '@air/types';
import { getClipSpritesheetFrameCount } from '@air/utils-clips';

import {
  type Dimension,
  getBackgroundPositionFromFrame,
  getSpriteStyles,
  SCRUBBABLE_THUMBNAIL_INDICATOR_WIDTH,
} from '~/utils/videoThumbnails';

export type UseVideoScrubbableThumbnailParams = {
  containerDimensions: Dimension;
  spritesheet: ClipSpriteSheet | undefined;
};

export const useVideoScrubbableThumbnail = ({
  containerDimensions,
  spritesheet,
}: UseVideoScrubbableThumbnailParams) => {
  const indicatorRef = useRef<HTMLDivElement>(null);
  const backgroundImageRef = useRef<HTMLDivElement>(null);

  const backgroundImageStyles = useMemo(() => {
    if (spritesheet) {
      return getSpriteStyles({
        containerWidth: containerDimensions.width,
        containerHeight: containerDimensions.height,
        spritesheet,
        frame: 0,
      });
    }

    return;
  }, [containerDimensions.height, containerDimensions.width, spritesheet]);

  const onMouseMove = useCallback(
    (event: MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      const { clientX, currentTarget } = event;
      const { left, width: containerWidth } = currentTarget.getBoundingClientRect();

      const pos = Math.max(0, Math.min(1, (clientX - left) / containerDimensions.width));
      const frameCount = getClipSpritesheetFrameCount(spritesheet);

      const currentFrame = Math.floor(pos * frameCount);
      const isLastFrame = currentFrame === frameCount;

      /**
       * Updates the indicator position
       */
      if (indicatorRef.current) {
        indicatorRef.current.style.display = 'block';
        indicatorRef.current.style.transform = `translateX(${
          pos * containerWidth - (isLastFrame ? SCRUBBABLE_THUMBNAIL_INDICATOR_WIDTH : 0)
        }px)`;
      }

      /**
       * Updates the background position to show the current frame
       */
      if (backgroundImageRef.current && spritesheet) {
        const { x, y } = getBackgroundPositionFromFrame({
          containerWidth: containerDimensions.width,
          containerHeight: containerDimensions.height,
          frame: currentFrame,
          spritesheet,
        });

        backgroundImageRef.current.style.backgroundPosition = `${x}px ${y}px`;
      }
    },
    [containerDimensions.width, containerDimensions.height, spritesheet],
  );

  const onMouseLeave = useCallback(() => {
    /**
     * Resets the background position back to the first frame
     */
    if (backgroundImageRef.current) {
      backgroundImageRef.current.style.backgroundPosition = `0 0`;
    }

    /**
     * Hides the indicator and resets its position
     */
    if (indicatorRef.current) {
      indicatorRef.current.style.display = 'none';
      indicatorRef.current.style.transform = 'translateX(0px)';
    }
  }, []);

  return {
    indicatorRef,
    backgroundImageRef,
    backgroundImageStyles,
    onMouseMove,
    onMouseLeave,
  };
};
